import { Module, Mutation, VuexModule, Action } from 'vuex-module-decorators';

import container from '@/container';
import { encodeParams } from '@/core/helpers';
import { FetchServiceContract, CONTRACTS } from '@/core/contracts';
import { ContextsState, SettingsState, ProductsState } from '@/store';

const fetchService = container.get<FetchServiceContract>(CONTRACTS.FetchService);

interface IndicatorsState {
    list: Indicator[];
    isLoading: boolean;
}

export enum IndicatorState {
    Value = 0,
    Error = 1,
    Empty = 2,
    NotAvailable = 3
}

export enum CustomValueType {
    Weight = 0,
    VehicleCount = 1,
}

export interface CustomValue {
    type?: CustomValueType;
    value: number | string;
    title?: string;
}

export interface Indicator {
    name: string;
    value: string | null;
    customValues?: CustomValue[];
    state: IndicatorState;
}

@Module({ name: 'indicators', namespaced: true })
export default class IndicatorsModule extends VuexModule implements IndicatorsState {
    private _list: Indicator[] = [];
    private _isLoading: boolean = false;

    public get list() {
        return this._list;
    }

    public get isLoading() {
        return this._isLoading;
    }

    @Action
    public async fetchIndicators(updateIndicators?: string[] | null) {
        const indicatorList = this._list;
        const acc: Indicator[] = [];

        this.setIndicators(acc);
        this.setIndicatorsLoading(true);
        const contextId = ContextsState.currentContextId;

        const indicatorsNames = SettingsState.contextSettings.indicators.length
            ? SettingsState.contextSettings.indicators
            : '';

        if (indicatorsNames) {
            if (updateIndicators) {
                updateIndicators = updateIndicators
                    .filter(
                        indicator => indicatorsNames.includes(indicator.toLocaleLowerCase())
                    );
            } else {
                updateIndicators = indicatorsNames;
            }

            const args: any = {
                names: updateIndicators.join(','),
            };

            if (ProductsState.current) {
                args.productAlias = ProductsState.current.alias;
            }

            const query = encodeParams(args);
            const url = `/contexts/${contextId}/specific-indicators?${query}`;
            const specifiedIndicators = await fetchService.get<Indicator[]>(url);
            // endpoint will returns indicators not in the same order, as they was posted
            // so we need to filter them in the same order as in indicatorsNames.
            // ! specific-indicators may not return information for one of the names.
            // ! this is the reason why reduce with check is used here.
            indicatorsNames.forEach(indicator => {
                let match = specifiedIndicators.find((i) =>
                    i.name.toLocaleLowerCase() === indicator.toLocaleLowerCase()
                );

                if (match) {
                    acc.push({
                        name: match.name,
                        value: match.value,
                        state: match.state,
                        customValues: match.customValues
                    });
                } else {
                    match = indicatorList.find((i) =>
                        i.name.toLocaleLowerCase() === indicator
                    );
                    if (match) {
                        acc.push({
                            name: match.name,
                            value: match.value,
                            state: match.state,
                            customValues: match.customValues
                        });
                    }
                }
            });

        }

        if (ContextsState.currentContextId === contextId) {
            this.setIndicators(acc);
        }
        this.setIndicatorsLoading(false);
    }

    @Mutation
    private setIndicators(payload: Indicator[]) {
        this._list = payload;
    }

    @Mutation
    private setIndicatorsLoading(payload: boolean) {
        this._isLoading = payload;
    }
}
